// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Alert, AlertTitle, LinearProgress } from '@mui/material';
import { AppIcon, AppName, ShowInApp } from '@shared/ui-kit';
import { isDefined } from '@shared/utils';
import styles from '@shared/ui-kit/components/loading-splash/loading-splash.module.css';
import { useMemo, type FC, type PropsWithChildren, type ReactNode } from 'react';
import {
  getAction,
  cleanErrorMessage,
  getTranslationKey,
} from '../../utils/error-codes';
import { ReconnectAccountButton } from '../reconnect-account-button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
  selectClientInstitution,
  selectClientInstitutions,
} from '@shared/data-access/slices/clients';
import { AdvisoryBankError } from '@shared/data-access/types/brokerage-types';

export type LoaderStatus =
  | 'uninitialized'
  | 'pending'
  | 'fulfilled'
  | 'rejected'
  | 'fetching';

export interface LoaderProps {
  readonly status: LoaderStatus;
  readonly shouldDisplayContent?: boolean;
  readonly shouldDisplayEmptyState?: boolean;
  readonly emptyStateTitle?: ReactNode;
  readonly emptyStateDescription?: ReactNode;
  readonly institution?: string;
  readonly actions?: ReactNode;
  readonly onRefresh?: () => void;
  readonly errorTitle?: ReactNode;
}

export const Loader: FC<PropsWithChildren<LoaderProps>> = ({
  status,
  children,
  shouldDisplayContent = true,
  institution: institutionId,
  shouldDisplayEmptyState,
  emptyStateTitle,
  emptyStateDescription,
  actions,
  onRefresh,
  errorTitle,
}) => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const institution = useSelector(state =>
    selectClientInstitution(state as any, institutionId),
  );

  const instititions = useSelector(selectClientInstitutions);

  const errorMessage = useMemo(() => institution?.error, [institution]);

  const action = useMemo(() => getAction(errorMessage), [errorMessage]);

  const translationKey = useMemo(
    () => getTranslationKey(errorMessage),
    [errorMessage],
  );

  const cleanedErrorMessage = useMemo(
    () => cleanErrorMessage(errorMessage),
    [errorMessage],
  );

  const isCredentialsError = useMemo(
    () =>
      errorMessage?.startsWith('2fa:') ||
      errorMessage?.startsWith('expired_token:'),
    [errorMessage],
  );

  const shouldShowManageAccounts =
    institutionId?.toLocaleLowerCase() === 'all' &&
    instititions.some(institution => institution.error !== null);

  const errorActionMessage: Record<AdvisoryBankError, ReactNode> = {
    [AdvisoryBankError.Ignore]: onRefresh ? (
      <ShowInApp apps={AppName.Client}>
        <span
          className="text-md font-semibold !text-warning underline transition-colors hover:cursor-pointer"
          onClick={onRefresh}
        >
          {t('aggregator.refresh', { ns: 'aggregator' })}
        </span>
      </ShowInApp>
    ) : (
      ''
    ),
    [AdvisoryBankError.Retry]: (
      <>
        <ShowInApp apps={AppName.Client}>
          <span
            className="text-md font-semibold !text-warning underline transition-colors hover:cursor-pointer"
            onClick={onRefresh}
          >
            {t('aggregator.refresh', { ns: 'aggregator' })}
          </span>
        </ShowInApp>

        <ShowInApp apps={AppName.Advisor}>
          {t('aggregator.contact-client', { ns: 'aggregator' })}
        </ShowInApp>
      </>
    ),
    [AdvisoryBankError.Reconnect]: (
      <>
        <ShowInApp apps={AppName.Client}>
          {shouldShowManageAccounts ? (
            <span
              className="text-md font-semibold !text-warning underline transition-colors hover:cursor-pointer"
              onClick={() => navigate('/banks')}
              role="button"
            >
              {t('aggregator.manage-accounts', { ns: 'aggregator' })}
            </span>
          ) : (
            <ReconnectAccountButton
              className="text-md font-semibold !text-warning underline transition-colors hover:cursor-pointer"
              institution={institutionId}
            />
          )}
        </ShowInApp>
        <ShowInApp apps={AppName.Advisor}>
          {t('aggregator.contact-client', { ns: 'aggregator' })}
        </ShowInApp>
      </>
    ),
  };

  return (
    <>
      <div
        className="mb-6 flex animate-fade items-center justify-center rounded-lg bg-section empty:hidden data-[status='rejected']:justify-start data-[status='rejected']:bg-transparent"
        data-testid="aggregator-loader"
        data-status={status}
      >
        {status === 'uninitialized' && (
          <div className="m-4 flex flex-col items-center justify-center gap-4 text-center">
            <p
              className="text-lg font-semibold text-primary-medium"
              aria-live="polite"
            >
              {t('aggregator.no-data', { ns: 'aggregator' })}
            </p>
            {isDefined(actions) && !!actions && (
              <div className="flex flex-row flex-wrap justify-center gap-3">
                {actions}
              </div>
            )}
          </div>
        )}

        {['pending', 'fetching'].includes(status) && (
          <div
            className="m-4 flex flex-col items-center justify-center gap-y-4"
            aria-live="polite"
            data-testid="aggregator-loader-loading"
          >
            <span
              className={styles['loading-splash-phrase-data']}
              lang={i18n.language}
            />
            <div className="w-60">
              <LinearProgress classes={{ root: 'rounded-lg' }} />
            </div>
          </div>
        )}

        {(status === 'rejected' ||
          ((errorMessage || shouldShowManageAccounts) &&
            !['fetching', 'pending'].includes(status))) && (
          <Alert
            severity="warning"
            icon={<AppIcon icon="Info" size="2xl" color="inherit" />}
            className="flex-1"
            classes={{
              root: '!bg-one-shot-bg !border-l-4 !border-warning !rounded-lg  ',
              icon: '!text-warning',
            }}
          >
            <AlertTitle className="!flex flex-col flex-wrap !items-start gap-2 !text-title-alt md:flex-row">
              {errorTitle ??
                (isCredentialsError
                  ? t('aggregator.credentials-update-error', { ns: 'aggregator' })
                  : t('aggregator.sync-error', { ns: 'aggregator' }))}
              &nbsp;
              {shouldShowManageAccounts ? (
                <>
                  <ShowInApp apps={AppName.Client}>
                    <span
                      className="!text-warning underline hover:cursor-pointer"
                      onClick={() => navigate('/banks')}
                      role="button"
                    >
                      {t('aggregator.manage-accounts', { ns: 'aggregator' })}
                    </span>
                  </ShowInApp>
                  <ShowInApp apps={AppName.Advisor}>
                    {t('aggregator.contact-client', { ns: 'aggregator' })}
                  </ShowInApp>
                </>
              ) : (
                errorActionMessage[action]
              )}
            </AlertTitle>

            {cleanedErrorMessage && (
              <p className="text-base font-normal italic !text-subtitle">
                {translationKey
                  ? t(translationKey, { ns: 'errors' })
                  : cleanedErrorMessage}
              </p>
            )}
          </Alert>
        )}
      </div>

      {status !== 'pending' &&
        shouldDisplayContent &&
        !shouldDisplayEmptyState &&
        children}

      {status === 'fulfilled' && shouldDisplayEmptyState && (
        <div className="flex flex-col items-center gap-6 rounded-lg bg-section p-4 text-center">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-primary-medium">
              {emptyStateTitle ?? t('common.no-data', { ns: 'common' })}
            </h3>
            <p className="text-base font-normal text-primary-medium">
              {emptyStateDescription ??
                t('aggregator.no-data-message', { ns: 'aggregator' })}
            </p>
          </div>
          {isDefined(actions) && !!actions && (
            <div className="flex flex-row flex-wrap justify-center gap-3">
              {actions}
            </div>
          )}
        </div>
      )}
    </>
  );
};
